import React, { memo, useRef, useEffect } from 'react'

import { Icon, ChevronDownLine, AIGenerated, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, string, bool, func, node } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

// Grace period before a hover-out closes the panel, so the pointer can cross the
// 1px seam between the trigger and the panel's hover bridge without it vanishing.
const HOVER_CLOSE_DELAY = 150

// Only "Create with AI" carries an icon today; keep the map so further iconed
// items (added to the backend `main_menu_items`) wire up without code changes.
const ICON_MAP = { ai: AIGenerated }
const ICON_SIZE = { width: '16', height: '16' }
const CHEVRON_SIZE = { width: '12', height: '12' }

function MenuItem({
  menuItem,
  isOpen = false,
  hoverEnabled = true,
  onToggle,
  onOpen = NOOP,
  onClose = NOOP,
  dropdown = null,
  hasPanel = false,
}) {
  const { id, title, href, icon, hasDropdown = false, dataTrackName } = menuItem
  const IconComponent = icon ? ICON_MAP[icon] : null
  // Sellers Hub opens a hover/focus panel; Menu flags it via `hasPanel` so the
  // panel wiring is active before the lazily-loaded `dropdown` content arrives.
  // The other `has_dropdown` triggers have no panel yet and keep a click toggle.
  // A truthy `dropdown` also implies a panel (backwards compatible for callers
  // that pass content directly).
  const isPanel = hasPanel || (hasDropdown && Boolean(dropdown))
  // Disclosure wiring: the trigger controls the panel by id (not an ARIA menu).
  const panelId = isPanel ? `${id}-panel` : undefined

  // Items without an href (Create with AI, Sellers Hub) are pure dropdown
  // triggers; items with an href (3D Models, 3D Printing) keep the link as a
  // fallback while still toggling. Hire a Freelancer has no dropdown and links out.
  const Wrapper = href ? 'a' : 'button'
  const wrapperProps = href ? { href } : { type: 'button' }
  const closeTimer = useRef(null)

  useEffect(() => () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
    }
  }, [])

  function cancelClose() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  function handleClick(event) {
    if (hasDropdown) {
      // Toggle on click/Enter for every dropdown trigger so keyboard and touch
      // users can open (and reopen) it; hover, when enabled, opens it in addition.
      event.preventDefault()
      onToggle(id)
    }
  }

  function handleEnter() {
    cancelClose()
    onOpen(id)
  }

  function handleLeave() {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      onClose()
    }, HOVER_CLOSE_DELAY)
  }

  function handleBlur(event) {
    // Close only when focus leaves the item entirely (not when moving into the panel).
    if (!event.currentTarget.contains(event.relatedTarget)) {
      cancelClose()
      onClose()
    }
  }

  // Desktop only: hover + focus open/close for the panel item (keyboard users
  // reach it via focus). On mobile the panel is click-toggled instead.
  const panelProps = isPanel && hoverEnabled
    ? {
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
      onFocus: handleEnter,
      onBlur: handleBlur,
    }
    : {}

  return (
    <li
      className={classnames(
        'unified-nav__menu-item',
        isOpen && 'is-open'
      )}
      {...panelProps}
    >
      <Wrapper
        {...wrapperProps}
        className={classnames(
          'unified-nav__menu-link',
          IconComponent && 'unified-nav__menu-link--brand'
        )}
        data-track-name={dataTrackName}
        aria-expanded={hasDropdown ? isOpen : undefined}
        aria-controls={panelId}
        onClick={handleClick}
      >
        <span className="unified-nav__menu-content">
          {IconComponent && (
            <Icon
              notSpaced
              color="dark"
              size={ICON_SIZE}
              svgComponent={IconComponent}
              className="unified-nav__menu-icon"
            />
          )}
          <span className="unified-nav__menu-label">{title}</span>
        </span>
        {hasDropdown && (
          <Icon
            notSpaced
            color="dark"
            size={CHEVRON_SIZE}
            svgComponent={ChevronDownLine}
            className="unified-nav__menu-chevron"
          />
        )}
      </Wrapper>
      {isPanel && isOpen && dropdown && React.cloneElement(dropdown, { id: panelId })}
    </li>
  )
}

MenuItem.propTypes = {
  menuItem: shape({
    id: string.isRequired,
    title: string.isRequired,
    href: string,
    icon: string,
    hasDropdown: bool,
    dataTrackName: string,
  }).isRequired,
  isOpen: bool,
  hoverEnabled: bool,
  onToggle: func.isRequired,
  onOpen: func,
  onClose: func,
  dropdown: node,
  hasPanel: bool,
}

export default memo(MenuItem)
